/**
 * LocalCalendarPage — Same rendering as PanchangamPage but reads from LOCAL JSON data
 * (bundled with app) instead of server API. This is the offline fallback.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  getPaksham, getVaasaram, getAyane, getRuthu, getMaseTamil,
  getNakshatraYukthayaam, getThithyam, getYogam, getYogam2,
  getchandrashtamam, getPoruththam, getRahuKala, getSunrise,
  getKuliga, getYamaKanda, getSoola, getGoodTime,
} from '../services/panchangam-utils';

const LocalCalendarPage = () => {
  const [pd, setPd] = useState(null);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(dayjs().format('YYYY-MM-DD'));
  const [computed, setComputed] = useState({});

  // Load ALL local data once
  useEffect(() => {
    axios.get('/data/panchangam_data-2025-to-2028.json')
      .then(r => setAllData(r.data || []))
      .catch(() => {});
  }, []);

  const fetchFromLocal = useCallback((dateStr) => {
    setLoading(true);
    const d = new Date(dateStr);
    const key = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    const found = allData.find(row => row.EnglishDate === key);
    if (found) {
      setPd(found);
      computeDerived(found);
    } else {
      setPd(null);
    }
    setLoading(false);
  }, [allData]);

  function computeDerived(data) {
    const paksham = getPaksham(data.PakshamNameInTamil, data.Paksham);
    const vasaram = getVaasaram(data.EnglishDay);
    const ayanam = getAyane(data.TamilMonthNumber);
    const ruthu = getRuthu(data.TamilMonthNumber);
    const masam = getMaseTamil(data.TamilMonthNumber);
    const nakshatram = getNakshatraYukthayaam(data.NakshatraNumber);
    const nakshatram2 = getNakshatraYukthayaam(data.Nakshatra2Number);
    const thithiyam = getThithyam(data.ThithiNumber);
    const thithyam2 = getThithyam(data.Thithi2Number);
    const yogam = getYogam(data.CalcDayYogamsShortInTamil);
    const yogam2 = getYogam2(data.CalcDayYogamsShortInTamil);
    const chandrasht1 = getchandrashtamam(data.NakshatraNumber);
    const chandrasht2 = getchandrashtamam(data.Nakshatra2Number);
    const porutham = getPoruththam(data.NakshatraNumber);
    const rahuKaalam = getRahuKala(data.EnglishDay);
    const suryaUdhayam = getSunrise(data.SuryaUdhayam);
    const kuligaiKaalam = getKuliga(data.EnglishDay);
    const yamakandam = getYamaKanda(data.EnglishDay);
    const soolam = getSoola(data.EnglishDay);
    const nallaNeram = getGoodTime(data.EnglishDay);

    setComputed({
      paksham, vasaram, ayanam, ruthu, masam, nakshatram, nakshatram2,
      thithiyam, thithyam2, yogam, yogam2, chandrasht1, chandrasht2,
      porutham, rahuKaalam, suryaUdhayam, kuligaiKaalam, yamakandam,
      soolam, nallaNeram,
    });
  }

  useEffect(() => { if (allData.length > 0) fetchFromLocal(today); }, [today, allData, fetchFromLocal]);

  const prevDate = () => setToday(dayjs(today).subtract(1, 'day').format('YYYY-MM-DD'));
  const nextDate = () => setToday(dayjs(today).add(1, 'day').format('YYYY-MM-DD'));

  const formatNumber = (val) => val != null ? Number(val).toFixed(2) : '';
  const formatEngDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2,'0')} - ${String(d.getMonth()+1).padStart(2,'0')} - ${d.getFullYear()}`;
  };

  if (loading) {
    return (
      <div style={{ textAlign:'center', padding:'60px 20px' }}>
        <p style={{ color:'#090b72', fontWeight:'bold' }}>Loading from local storage....</p>
      </div>
    );
  }

  if (!pd) {
    return (
      <div style={{ textAlign:'center', padding:'60px 20px' }}>
        <p>இந்த நாளுக்கு உள்ளூர் தரவு கிடைக்கவில்லை (2025-2028 மட்டும்)</p>
        <button onClick={prevDate} style={{ margin:'10px', padding:'8px 16px' }}>← முன்</button>
        <button onClick={nextDate} style={{ margin:'10px', padding:'8px 16px' }}>பின் →</button>
      </div>
    );
  }

  const c = computed;

  return (
    <div>
      {/* ── Source indicator ── */}
      <div style={{ backgroundColor:'#fff3e0', textAlign:'center', padding:'4px', fontSize:'0.7rem', fontWeight:600, color:'#e65100' }}>
        📱 உள்ளூர் தரவு (Local Storage) — ஆஃப்-லைனில் கிடைக்கும்
      </div>

      {/* ── Blue Header with arrows (same as PanchangamPage) ── */}
      <div style={{ width:'100%', backgroundColor:'#090b72', borderBottom:'solid 2px orange', borderTop:'solid 2px orange', display:'flex', alignItems:'center' }}>
        <div style={{ width:'10%', textAlign:'center', cursor:'pointer', padding:'12px 0' }} onClick={prevDate}>
          <span style={{ color:'white', fontSize:'24px' }}>◀</span>
        </div>
        <div style={{ width:'80%', textAlign:'center', color:'white', fontWeight:700, fontSize:'2.2vh', padding:'8px 0' }}>
          {pd.YearNameInTamil}-{pd.AyanamNameInTamil}<br/>
          <span style={{ color:'rgb(152, 248, 243)', fontSize:'0.9rem' }}>
            {pd.RuthuNameInTamil}-ருது-{pd.TamilMonth}-{pd.TamilDate}
          </span><br/>
          <span style={{ color:'yellow', fontSize:'1.0rem' }}>
            {formatEngDate(pd.EnglishDate)} - {pd.DayNameInTamil}
          </span>
        </div>
        <div style={{ width:'10%', textAlign:'center', cursor:'pointer', padding:'12px 0' }} onClick={nextDate}>
          <span style={{ color:'white', fontSize:'24px' }}>▶</span>
        </div>
      </div>

      {/* ── Panchangam Rows (identical to PanchangamPage) ── */}
      <div style={{ marginLeft:'-2px' }}>
        <Row label="பக்ஷ-திதி" value={<>{c.paksham}-{pd.ThithiNameInTamil} ({formatNumber(pd.ThithiDuration)})&nbsp;IST <span style={{color:'rgb(242, 77, 0)'}}>வரை</span></>} />
        <Row label="நக்ஷத்ரம்" value={<>{pd.NakshatraNameInTamil} ({formatNumber(pd.NakshatraDuration)})&nbsp;IST <span style={{color:'rgb(242, 77, 0)'}}>வரை</span></>} />
        <Row label="யோகம்" value={<>{pd.YogaNameInTamil} ({formatNumber(pd.YogaDuration)})IST &nbsp;<span style={{color:'rgb(242, 77, 0)'}}>வரை</span></>} />
        <Row label="அ-சி யோக" value={<>{c.yogam}-{formatNumber(pd.NakshatraDuration)}&nbsp;<span style={{color:'rgb(242, 77, 0)'}}>&nbsp;&gt;&gt;&nbsp;</span>{c.yogam2}</>} />
        <Row label="கரண-யோ" value={<>{pd.KaranamNameInTamil} ({formatNumber(pd.KaranaDuration)}) &nbsp;<span style={{color:'rgb(242, 77, 0)',fontSize:'12px'}}>- யோகி: -</span>{pd.Yogini}</>} />
        <Row label="நேத்-ஜீவன்" value={<><span style={{color:'rgb(242, 77, 0)'}}>நேத்ரம்:</span> {pd.Nethram}&nbsp;&nbsp;&nbsp;<span style={{color:'rgb(242, 77, 0)'}}>ஜீவன்:</span> {pd.Jeevan}</>} />
        <Row label="சூர்(சென்)" value={<><span style={{color:'rgb(242, 77, 0)'}}>உதய</span> {formatNumber(pd.SuryaUdhayam)} <span style={{color:'rgb(242, 77, 0)'}}>அஸ்த</span> {formatNumber(pd.AgasDuration)}</>} />
        <Row label="ராகுகால" value={<>{c.rahuKaalam} &nbsp;<span style={{color:'rgb(242, 77, 0)',fontSize:'0.85rem'}}>{c.suryaUdhayam} Mins SunRise</span></>} />
        <Row label="எமகண்டம்" value={<>{c.yamakandam} &nbsp;<span style={{color:'rgb(242, 77, 0)',fontSize:'0.85rem'}}>{c.suryaUdhayam} Mins SunRise</span></>} />
        <Row label="குளிகை" value={<>{c.kuligaiKaalam}&nbsp;<span style={{color:'rgb(242, 77, 0)',fontSize:'0.85rem'}}>{c.suryaUdhayam} Mins SunRise</span></>} />
        <Row label="சூலம்:" value={c.soolam} smallFont />
        <Row label="சந்த்ராஷ்ட" value={<>{c.chandrasht1} {formatNumber(pd.NakshatraDuration)}&nbsp;<span style={{color:'rgb(242, 77, 0)'}}>&gt;&gt;</span>&nbsp;{c.chandrasht2}</>} />
        <Row label="நல்லநேரம்" value={c.nallaNeram} smallFont />
        <Row label="ச்ராத்-திதி" value={<>{pd.Paksham2NameInTamil}-{pd.ShraadhdhaThithiNameInTamil}</>} />
        <Row label="பொருத்தம்" value={<>{c.porutham} -<span style={{fontSize:'0.65rem'}}>செட்களுக்குப் பொருந்தும்</span></>} smallLabel />
      </div>

      {/* ── Scrolling notice ── */}
      <div style={{ overflow:'hidden', whiteSpace:'nowrap' }}>
        <span style={{ color:'red', backgroundColor:'yellow', fontSize:'0.8rem', fontWeight:600, textAlign:'center', display:'block', padding:'4px' }}>
          📱 LOCAL DATA (2025-2028) || All Times 24hr IST || 24=Midnight, 25=1am, ... 29=5am
        </span>
      </div>

      {/* ── Day Specials ── */}
      <div style={{ textAlign:'center', backgroundColor:'#015540', color:'white', padding:'6px', fontWeight:'bold', fontSize:'0.9rem' }}>
        {formatEngDate(pd.EnglishDate)} - விசேஷங்கள்
      </div>
      <div style={{ padding:'8px 12px', fontSize:'0.9rem', color:'#333', backgroundColor:'#f9f9f9' }}>
        {pd.DaySpecialityInTamil || 'விசேஷம் இல்லை'}
      </div>

      {/* ── Sankalpam ── */}
      <div style={{ textAlign:'center', backgroundColor:'#015540', color:'white', padding:'6px', fontWeight:'bold', fontSize:'0.9rem' }}>
        {formatEngDate(pd.EnglishDate)} ஸங்கல்பம்
      </div>
      <div style={{ padding:'10px 12px', fontSize:'0.85rem', color:'#333', backgroundColor:'#fffef0', lineHeight:'1.6' }}>
        {pd.YearNameInTamil} நாம ஸம்வத்ஸரே - {c.ayanam} - {c.ruthu}-ருதௌ - {c.masam} மாஸே - {c.paksham}பக்ஷே -
        {c.thithiyam}-({formatNumber(pd.ThithiDuration)}) சுப திதௌ -
        <span style={{backgroundColor:'#F2E100'}}>(அடுத்த திதி: {c.thithyam2})</span>
        - வாஸர: {c.vasaram} வாஸர யுக்தாயாம் - {c.nakshatram} ({formatNumber(pd.NakshatraDuration)})
        நக்ஷத்ர யுக்தாயம்
        <span style={{backgroundColor:'#F2E100'}}>(அடுத்த நக்ஷத்ரம்: {c.nakshatram2})</span>
        - {pd.YogaNameInTamil}({formatNumber(pd.YogaDuration)}) யோக யுக்தாயாம்
        <span style={{backgroundColor:'#F2E100'}}>(அடுத்த யோகம்: {pd.Yoga2NameInTamil})</span>
        - {pd.KaranamNameInTamil}({formatNumber(pd.KaranaDuration)}) கரண யுக்தாயாம்
        <span style={{backgroundColor:'#F2E100'}}>(அடுத்த கரணம்: {pd.Karanam2NameInTamil})</span>
        ஏவங்குண விசேஷண விசிஷ்டாயாம் அஸ்யாம் {c.thithiyam} சுப / புண்ய திதௌ .......
      </div>

      {/* ── Date Picker ── */}
      <div style={{ textAlign:'center', padding:'16px', fontSize:'0.9rem' }}>
        தேவை தேதிக்கு தகவல் பெற தேதி தேர்வு செய்யவும்
        <div style={{ margin:'12px auto', maxWidth:'300px' }}>
          <input type="date" value={today} onChange={(e) => setToday(e.target.value)}
            style={{ width:'100%', padding:'10px', fontSize:'16px', borderRadius:'8px', border:'2px solid #090b72', textAlign:'center' }} />
        </div>
      </div>
    </div>
  );
};

// ── Row Component (same as PanchangamPage) ──
const Row = ({ label, value, smallFont, smallLabel }) => (
  <div style={{ display:'flex', borderBottom:'1px solid #ddd', fontSize:'0.9rem' }}>
    <div style={{ width:'28%', padding:'6px 8px', backgroundColor:'#e8f5e9', fontWeight:'bold', color:'#1b5e20', fontSize: smallLabel ? '0.7rem' : '0.85rem' }}>
      {label}
    </div>
    <div style={{ width:'72%', padding:'6px 8px', fontSize: smallFont ? '0.80rem' : '0.9rem' }}>
      {value}
    </div>
  </div>
);

export default LocalCalendarPage;